import { Badge, ShareOn } from '@mdxui/primitives'
import { cn } from '@mdxui/primitives/lib/utils'
import { ArrowLeft } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

export interface BlogPostHeaderLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface BlogPostHeaderProps {
  /** Post title */
  title: string
  /** Post description/excerpt */
  description?: string
  /** Post tags/categories */
  tags?: string[]
  /** Formatted publication date */
  date?: string
  /** Back link href */
  backHref?: string
  /** Back link label */
  backLabel?: string
  /** URL for sharing (defaults to current page) */
  shareUrl?: string
  /** Custom link component */
  LinkComponent?: ComponentType<BlogPostHeaderLinkProps>
  /** Additional className */
  className?: string
}

/**
 * Default link component
 */
function DefaultLink({ href, children, className }: BlogPostHeaderLinkProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}

export function BlogPostHeader({
  title,
  description,
  tags,
  date,
  backHref = '/blog',
  backLabel = 'Back',
  shareUrl = typeof window !== 'undefined' ? window.location.href : '',
  LinkComponent = DefaultLink,
  className,
}: BlogPostHeaderProps) {
  return (
    <div className={cn('pt-6 sm:pt-12 pb-8 border-b border-border relative z-10', className)}>
      <div className="max-w-7xl mx-auto flex flex-col gap-6 px-6">
        {/* Top: Back button */}
        <div>
          <LinkComponent
            href={backHref}
            className="flex items-center gap-1.5 text-sm hover:text-card-foreground text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
            {backLabel}
          </LinkComponent>
        </div>

        {/* Middle: Title and Description */}
        <div className="flex flex-col gap-4">
          <h1 className="text-4xl font-semibold text-card-foreground">{title}</h1>
          {description && (
            <p className="text-muted-foreground max-w-4xl md:text-lg md:text-balance">{description}</p>
          )}
        </div>

        {/* Bottom: Category badges + date (left) and ShareOn (right) */}
        <div className="flex flex-wrap items-center justify-between gap-3 gap-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-3">
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag: string) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {tags && tags.length > 0 && date && (
              <div className="hidden sm:block h-4 w-px bg-border" />
            )}
            {date && (
              <time className="text-xs text-muted-foreground font-mono">{date}</time>
            )}
          </div>
          <ShareOn url={shareUrl} text={title} />
        </div>
      </div>
    </div>
  )
}
